/**
 * Overlay primitives: Modal, Sheet (mobile bottom sheet with snap points), Toast.
 */
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { create } from 'zustand';
import { Icon, type IconName } from './Icon';
import { cx, IconButton } from './primitives';

// ───────────── Modal ─────────────

export interface ModalProps {
  open: boolean;
  onClose(): void;
  title: ReactNode;
  eyebrow?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  /** Disable close on backdrop / Escape (e.g. while submitting). */
  locked?: boolean;
  /** data-testid on the dialog element. */
  testId?: string;
}

const modalStack: symbol[] = [];
let previousRootInert = false;
export function Modal({ open, onClose, title, eyebrow, children, footer, size = 'md', locked, testId }: ModalProps) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement as HTMLElement | null;
    const token=Symbol('modal');
    modalStack.push(token);
    const onKey = (e: KeyboardEvent) => {
      if (modalStack.at(-1)!==token) return;
      if (e.key === 'Escape' && !locked) onClose();
      if (e.key === 'Tab' && ref.current) {
        const f = ref.current.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        const list = Array.from(f).filter((el) => !el.hasAttribute('disabled'));
        if (!list.length) return;
        const first = list[0]!;
        const last = list[list.length - 1]!;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    // background is inert (not focusable / not in the accessibility tree) while the modal is open
    const root = document.getElementById('root');
    if(modalStack.length===1)previousRootInert=root?.inert??false;
    if (root) root.inert = true;
    const t = setTimeout(() => {
      const target = ref.current?.querySelector<HTMLElement>('[autofocus], input, select, textarea, button.btn--primary') ?? ref.current;
      target?.focus();
    }, 10);
    return () => {
      document.removeEventListener('keydown', onKey);
      clearTimeout(t);
      const index=modalStack.indexOf(token);if(index>=0)modalStack.splice(index,1);
      if(root&&modalStack.length===0)root.inert=previousRootInert;
      if(prev?.isConnected&&!prev.closest('[inert]'))prev.focus();
    };
  }, [open, locked, onClose]);

  // Phone: the modal is a bottom sheet; drag its handle/header down to dismiss.
  const drag = useRef<{ y: number; t: number; dy: number } | null>(null);
  const onGrabDown = (e: React.PointerEvent) => {
    if (locked || !window.matchMedia?.('(max-width: 640px)').matches) return;
    if ((e.target as HTMLElement).closest('button, a, input, select, textarea')) return;
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    drag.current = { y: e.clientY, t: performance.now(), dy: 0 };
    ref.current?.classList.add('is-dragging');
  };
  const onGrabMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d || !ref.current) return;
    d.dy = Math.max(0, e.clientY - d.y);
    ref.current.style.transform = d.dy ? `translateY(${d.dy}px)` : '';
  };
  const onGrabUp = (cancelled = false) => {
    const d = drag.current;
    drag.current = null;
    const m = ref.current;
    if (!d || !m) return;
    m.classList.remove('is-dragging');
    const velocity = d.dy / Math.max(1, performance.now() - d.t);
    if (!locked && !cancelled && (d.dy > 120 || (d.dy > 24 && velocity > 0.6))) {
      onClose();
      return;
    }
    m.classList.add('is-settling');
    m.style.transform = '';
    window.setTimeout(() => m.classList.remove('is-settling'), 260);
  };
  const grab = { onPointerDown: onGrabDown, onPointerMove: onGrabMove, onPointerUp: () => onGrabUp(), onPointerCancel: () => onGrabUp(true) };

  if (!open) return null;
  return createPortal(
    <div className="modal-root">
      <div className="modal-backdrop" onClick={() => !locked && onClose()} />
      <div ref={ref} className={cx('modal', `modal--${size}`)} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} data-testid={testId}>
        <div className="modal__grab" aria-hidden {...grab}>
          <span />
        </div>
        <header className="modal__head" {...grab}>
          <div>
            {eyebrow ? <div className="eyebrow">{eyebrow}</div> : null}
            <h2 className="modal__title" id={titleId}>{title}</h2>
          </div>
          <IconButton icon="close" label="Close" onClick={onClose} disabled={locked} />
        </header>
        <div className="modal__body">{children}</div>
        {footer ? <footer className="modal__foot">{footer}</footer> : null}
      </div>
    </div>,
    document.body,
  );
}

// ───────────── Sheet ─────────────

export type SheetSnap = 'peek' | 'half' | 'full';

export interface SheetProps {
  snap: SheetSnap;
  onSnapChange(s: SheetSnap): void;
  /** Always-visible header (drag area). */
  header?: ReactNode;
  children: ReactNode;
  /** Height in px of the peek state (header + a line). Default 112. */
  peekHeight?: number;
  /** Space reserved at the bottom (e.g. the tab bar). Default: var(--tabbar-h) + safe area. */
  bottomOffset?: string;
  className?: string;
  /** Which snaps are allowed. */
  snaps?: SheetSnap[];
  ariaLabel?: string;
}

/**
 * Mobile bottom sheet with a drag handle and three snap points.
 * Drag the handle/header; flicks (> 0.5 px/ms) advance one snap in that direction.
 */
export function Sheet({
  snap,
  onSnapChange,
  header,
  children,
  peekHeight = 112,
  bottomOffset = 'calc(var(--tabbar-h) + var(--safe-bottom))',
  className,
  snaps = ['peek', 'half', 'full'],
  ariaLabel = 'Details',
}: SheetProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [vh, setVh] = useState(() => (typeof window !== 'undefined' ? window.innerHeight : 800));
  const [drag, setDrag] = useState<number | null>(null);
  const start = useRef<{ y: number; h: number; t: number; lastY: number; lastT: number } | null>(null);

  useLayoutEffect(() => {
    const on = () => setVh(window.innerHeight);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);

  const heights = useCallback(
    (s: SheetSnap) => {
      if (s === 'peek') return peekHeight;
      if (s === 'half') return Math.round(vh * 0.46);
      return Math.round(vh * 0.86);
    },
    [peekHeight, vh],
  );

  const current = drag ?? heights(snap);

  const onPointerDown = (e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest('button, a, input, select, textarea')) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    start.current = { y: e.clientY, h: heights(snap), t: performance.now(), lastY: e.clientY, lastT: performance.now() };
    setDrag(heights(snap));
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const s = start.current;
    if (!s) return;
    const h = Math.max(peekHeight * 0.6, Math.min(vh * 0.92, s.h + (s.y - e.clientY)));
    s.lastY = e.clientY;
    s.lastT = performance.now();
    setDrag(h);
  };
  const onPointerUp = (e: React.PointerEvent) => {
    const s = start.current;
    start.current = null;
    if (!s) return;
    const h = drag ?? s.h;
    setDrag(null);
    const dt = Math.max(1, performance.now() - s.t);
    const velocity = (s.y - e.clientY) / dt; // px/ms, +ve = up
    const order = snaps;
    const idx = order.indexOf(snap);
    if (Math.abs(s.y - e.clientY) < 6) {
      // tap on the handle toggles peek ↔ half
      onSnapChange(snap === 'peek' ? (order.includes('half') ? 'half' : order[order.length - 1]!) : 'peek');
      return;
    }
    if (Math.abs(velocity) > 0.5) {
      const next = order[Math.max(0, Math.min(order.length - 1, idx + (velocity > 0 ? 1 : -1)))]!;
      onSnapChange(next);
      return;
    }
    let best = order[0]!;
    for (const o of order) if (Math.abs(heights(o) - h) < Math.abs(heights(best) - h)) best = o;
    onSnapChange(best);
  };

  return (
    <div
      ref={ref}
      className={cx('sheet', drag !== null && 'is-dragging', `sheet--${snap}`, className)}
      style={{ height: current, bottom: bottomOffset,
        maxHeight: `calc(100dvh - var(--topbar-h) - var(--safe-top) - ${bottomOffset})` }}
      role="region"
      aria-label={ariaLabel}
    >
      <div className="sheet__grab" onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={() => { start.current = null; setDrag(null); }}>
        <button
          type="button"
          className="sheet__handle"
          aria-label={snap === 'peek' ? 'Expand' : 'Collapse'}
          onClick={() => onSnapChange(snap === 'peek' ? 'half' : 'peek')}
        >
          <span />
        </button>
        {header ? <div className="sheet__header">{header}</div> : null}
      </div>
      <div className="sheet__body">{children}</div>
    </div>
  );
}

// ───────────── Toast ─────────────

export type ToastTone = 'neutral' | 'success' | 'warning' | 'alert' | 'advisory';

export interface ToastItem {
  id: number;
  title: string;
  body?: string;
  tone: ToastTone;
  icon?: IconName;
  timeoutMs: number;
  action?: { label: string; onClick(): void };
}

interface ToastStore {
  items: ToastItem[];
  show(t: Partial<Omit<ToastItem, 'id'>> & { title: string }): number;
  dismiss(id: number): void;
}

let nextToastId = 1;
/** Older toasts beyond this are dropped (the host shows 2–3 and folds the rest into "+n more"). */
const TOAST_KEEP = 8;
export const useToasts = create<ToastStore>((set, get) => ({
  items: [],
  show(t) {
    const id = nextToastId++;
    const item: ToastItem = { id, tone: 'neutral', timeoutMs: 5000, ...t };
    set({ items: [...get().items.slice(-(TOAST_KEEP - 1)), item] });
    if (item.timeoutMs > 0) setTimeout(() => get().dismiss(id), item.timeoutMs);
    return id;
  },
  dismiss(id) {
    set({ items: get().items.filter((x) => x.id !== id) });
  },
}));

/** Imperative helper: toast('Saved'), toast({ title, body, tone }). */
export function toast(t: string | (Partial<Omit<ToastItem, 'id'>> & { title: string })): number {
  return useToasts.getState().show(typeof t === 'string' ? { title: t } : t);
}

const TOAST_ICON: Record<ToastTone, IconName> = { neutral: 'info', success: 'check', warning: 'alert', alert: 'alert', advisory: 'bell' };
const PHONE_MQ = '(max-width: 767px)';
function usePhoneLane() {
  return useSyncExternalStore(
    (cb) => {
      if (typeof window === 'undefined' || !window.matchMedia) return () => undefined;
      const mq = window.matchMedia(PHONE_MQ);
      mq.addEventListener('change', cb);
      return () => mq.removeEventListener('change', cb);
    },
    () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(PHONE_MQ).matches : false),
    () => false,
  );
}

/**
 * Toast lane. Newest first; at most 2 (phone) / 3 (wider) show and the rest fold into "+n more".
 * Each toast swipes away sideways (or up on a phone) and has a 44 px close button.
 */
export function ToastHost() {
  const items = useToasts((s) => s.items);
  const dismiss = useToasts((s) => s.dismiss);
  const phone = usePhoneLane();
  const [expanded, setExpanded] = useState(false);
  const limit = phone ? 2 : 3;
  const ordered = [...items].reverse();
  const open = expanded && ordered.length > limit;
  const shown = open ? ordered : ordered.slice(0, limit);
  const hidden = ordered.length - shown.length;
  useEffect(() => {
    if (expanded && items.length <= limit) setExpanded(false);
  }, [expanded, items.length, limit]);
  return createPortal(
    <div className="toasts" aria-live="polite">
      {shown.map((t) => (
        <ToastView key={t.id} t={t} phone={phone} onDismiss={() => dismiss(t.id)} />
      ))}
      {hidden > 0 || open ? (
        <div className="toasts__bar">
          {open ? (
            <button type="button" className="toasts__more" onClick={() => items.forEach((t) => dismiss(t.id))}>
              Clear all
            </button>
          ) : null}
          <button type="button" className="toasts__more" aria-expanded={open} onClick={() => setExpanded((v) => !v)}>
            {open ? 'Show less' : `+${hidden} more`}
          </button>
        </div>
      ) : null}
    </div>,
    document.body,
  );
}

function ToastView({ t, phone, onDismiss }: { t: ToastItem; phone: boolean; onDismiss(): void }) {
  const ref = useRef<HTMLDivElement>(null);
  const drag = useRef<{ x: number; y: number; dx: number; dy: number; id: number } | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const leave = (dir: { x: number; y: number }) => {
    const el = ref.current;
    setLeaving(true);
    if (el) el.style.transform = `translate(${dir.x}px, ${dir.y}px)`;
    window.setTimeout(onDismiss, matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 0 : 180);
  };
  const onDown = (e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest('.icon-btn, .toast__action')) return;
    drag.current = { x: e.clientX, y: e.clientY, dx: 0, dy: 0, id: e.pointerId };
  };
  const onMove = (e: React.PointerEvent) => {
    const d = drag.current;
    const el = ref.current;
    if (!d || !el) return;
    d.dx = e.clientX - d.x;
    d.dy = Math.min(0, e.clientY - d.y);
    if (Math.abs(d.dx) < 6 && Math.abs(d.dy) < 6) return;
    if (!el.classList.contains('is-dragging')) {
      el.classList.add('is-dragging');
      el.setPointerCapture?.(d.id);
    }
    const horizontal = Math.abs(d.dx) >= Math.abs(d.dy) || !phone;
    el.style.transform = horizontal ? `translateX(${d.dx}px)` : `translateY(${d.dy}px)`;
    el.style.opacity = String(Math.max(0.3, 1 - (horizontal ? Math.abs(d.dx) / 240 : Math.abs(d.dy) / 120)));
  };
  const onUp = () => {
    const d = drag.current;
    const el = ref.current;
    drag.current = null;
    if (!d || !el || !el.classList.contains('is-dragging')) return;
    el.classList.remove('is-dragging');
    if (Math.abs(d.dx) > 72) return leave({ x: Math.sign(d.dx) * 420, y: 0 });
    if (phone && d.dy < -36) return leave({ x: 0, y: -120 });
    el.style.transform = '';
    el.style.opacity = '';
  };
  const long = !!t.body || t.title.length > 34;
  return (
    <div
      ref={ref}
      className={cx('toast', `toast--${t.tone}`, expanded && 'is-expanded', leaving && 'is-leaving')}
      role={t.tone === 'alert' || t.tone === 'warning' ? 'alert' : 'status'}
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerCancel={onUp}
    >
      <Icon name={t.icon ?? TOAST_ICON[t.tone]} size={16} className="toast__icon" />
      {/* tap the text to read a clamped toast in full (visual clamp only: assistive tech reads it all) */}
      <div className={cx('toast__text', long && 'is-clampable')} onClick={long ? () => setExpanded((v) => !v) : undefined}>
        <div className="toast__title">{t.title}</div>
        {t.body ? <div className="toast__body">{t.body}</div> : null}
      </div>
      {t.action ? (
        <button
          type="button"
          className="toast__action"
          onClick={() => {
            t.action!.onClick();
            onDismiss();
          }}
        >
          {t.action.label}
        </button>
      ) : null}
      <IconButton icon="close" label="Dismiss" size="sm" onClick={onDismiss} />
    </div>
  );
}

// ───────────── Popover (anchored menu) ─────────────

export function Popover({
  open,
  onClose,
  anchor,
  children,
  align = 'end',
  placement = 'bottom',
  className,
}: {
  open: boolean;
  onClose(): void;
  anchor: React.RefObject<HTMLElement | null>;
  children: ReactNode;
  align?: 'start' | 'end';
  /** 'top' opens upward (anchors near the bottom of the screen: transport bars, MARK rows). */
  placement?: 'bottom' | 'top';
  className?: string;
}) {
  const [pos, setPos] = useState<{ top?: number; bottom?: number; left?: number; right?: number; maxHeight: number } | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (!open || !anchor.current) return;
    const r = anchor.current.getBoundingClientRect();
    // never taller than the space below the anchor: a long menu scrolls instead of running off screen
    const v = placement === 'top' ? { bottom: window.innerHeight - r.top + 6, maxHeight: Math.max(160, r.top - 14) } : { top: r.bottom + 6, maxHeight: Math.max(160, window.innerHeight - r.bottom - 14) };
    setPos(align === 'end' ? { ...v, right: Math.max(8, window.innerWidth - r.right) } : { ...v, left: Math.max(8, r.left) });
  }, [open, anchor, align, placement]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (ref.current?.contains(e.target as Node) || anchor.current?.contains(e.target as Node)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose, anchor]);
  if (!open || !pos) return null;
  return createPortal(
    <div ref={ref} className={cx('popover', align === 'start' && 'popover--start', placement === 'top' && 'popover--top', className)} style={pos} role="menu">
      {children}
    </div>,
    document.body,
  );
}

export function MenuItem({ icon, children, onClick, danger, right }: { icon?: IconName; children: ReactNode; onClick(): void; danger?: boolean; right?: ReactNode }) {
  return (
    <button type="button" role="menuitem" className={cx('menu-item', danger && 'menu-item--danger')} onClick={onClick}>
      {icon ? <Icon name={icon} size={16} /> : <span className="menu-item__spacer" />}
      <span className="menu-item__label">{children}</span>
      {right ? <span className="menu-item__right">{right}</span> : null}
    </button>
  );
}
